import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLabelCleaner, joinBrandSuffix } from '../../utils/brandLabel';
import { haptics } from '../../utils/haptics';
import { clearCache } from '../../utils/apiCache';
import apiClient from '../../utils/apiClient';
import {
  LayoutDashboard,
  FolderLock,
  MessageSquare,
  Users,
  Sparkles,
  CheckSquare,
  Shield,
  Settings,
  LogOut,
  Moon,
  Sun,
  Home,
  Headphones,
  ShieldCheck,
  CloudOff,
  KeyRound,
  Clock,
  CreditCard,
  PanelLeftClose,
  PanelLeftOpen,
  Megaphone,
  HeartPulse,
  AlertTriangle,
  BookOpen,
  Search,
  StickyNote,
  Gift,
  Activity,
  Plus,
  Heart,
  Star,
  Check,
  MessageCircle,
  DollarSign,
  Briefcase
} from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from '../../utils/toast';
import NotificationBell from '../NotificationBell';
import SidebarPillButton from './SidebarPillButton';
import PublicDeviceModeMenuButton from './PublicDeviceModeMenuButton';
import AdminSectionNav from './AdminSectionNav';
import BenefactorSectionNav from './BenefactorSectionNav';
import PlatformBooleanToggle from './PlatformBooleanToggle';
import { isPlatformSubscriptionsVisible, setPlatformSubscriptionsFlag, PLATFORM_SUBSCRIPTIONS_FLAG_EVENT } from '../../utils/platformSubscriptionsFlag';
import { API_URL } from '../../config';
import { filterNavByFeatures } from '../../utils/featureGates';
import { applyUserMenuOrder } from '../../config/menuRegistry';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const OtpToggle = ({ collapsed }) => {
  const [otpDisabled, setOtpDisabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('carryon_token');
    if (token) {
      apiClient.get(`${API_URL}/admin/platform-settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setOtpDisabled(!!res.data?.otp_disabled))
        .catch((err) => {
          if (err?.response?.status && err.response.status !== 401) {
            try { toast.error("Couldn't read OTP state — tap to retry."); } catch {}
          }
        });
    }
  }, []);
  const toggle = async () => {
    if (busy) return;
    const newVal = !otpDisabled;
    setOtpDisabled(newVal);
    setBusy(true);
    const token = localStorage.getItem('carryon_token');
    try {
      const res = await apiClient.put(
        `${API_URL}/admin/platform-settings`,
        { otp_disabled: newVal },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      const authoritative = !!res.data?.otp_disabled;
      setOtpDisabled(authoritative);
      try { toast.success(authoritative ? 'OTP disabled platform-wide' : 'OTP enabled platform-wide'); } catch {}
    } catch (err) {
      setOtpDisabled(!newVal);
      const detail = err?.response?.data?.detail || err?.message || 'request failed';
      try { toast.error(`Could not update OTP setting — ${detail}`); } catch {}
    } finally {
      setBusy(false);
    }
  };
  if (collapsed) {
    return (
      <div className="mx-1 my-2 flex items-center justify-center px-2 py-2 rounded-lg cursor-pointer" onClick={toggle} title={`OTP ${otpDisabled ? 'Disabled' : 'Enabled'}`} style={{ background: otpDisabled ? 'rgba(239,68,68,0.06)' : 'var(--s)', border: `1px solid ${otpDisabled ? 'rgba(239,68,68,0.2)' : 'var(--b)'}` }}>
        <ShieldCheck className="w-5 h-5" style={{ color: otpDisabled ? '#ef4444' : '#10b981' }} />
      </div>
    );
  }
  return (
    <div className="mx-3 my-2 flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: otpDisabled ? 'rgba(239,68,68,0.06)' : 'var(--s)', border: `1px solid ${otpDisabled ? 'rgba(239,68,68,0.2)' : 'var(--b)'}` }}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" style={{ color: otpDisabled ? '#ef4444' : '#10b981' }} />
        <span className="text-xs font-bold text-[var(--t)]">OTP</span>
      </div>
      <Switch checked={!otpDisabled} onCheckedChange={toggle} data-testid="sidebar-otp-toggle" />
    </div>
  );
};

// Apr 27, 2026 — distinct toggle for the signup-OTP gate. Same look-and-feel
// as OtpToggle but flips `signup_otp_disabled`. Lets the founder skip email
// verification for new signups during QA / automation runs.
const SignupOtpToggle = ({ collapsed }) => {
  const [signupOtpDisabled, setSignupOtpDisabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('carryon_token');
    if (token) {
      apiClient.get(`${API_URL}/admin/platform-settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setSignupOtpDisabled(!!res.data?.signup_otp_disabled))
        .catch((err) => {
          if (err?.response?.status && err.response.status !== 401) {
            try { toast.error("Couldn't read signup-OTP state — tap to retry."); } catch {}
          }
        });
    }
  }, []);
  const toggle = async () => {
    if (busy) return;
    const newVal = !signupOtpDisabled;
    setSignupOtpDisabled(newVal);
    setBusy(true);
    const token = localStorage.getItem('carryon_token');
    try {
      const res = await apiClient.put(
        `${API_URL}/admin/platform-settings`,
        { signup_otp_disabled: newVal },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      const authoritative = !!res.data?.signup_otp_disabled;
      setSignupOtpDisabled(authoritative);
      try { toast.success(authoritative ? 'Signup OTP gate DISABLED' : 'Signup OTP gate ENABLED'); } catch {}
    } catch (err) {
      setSignupOtpDisabled(!newVal);
      const detail = err?.response?.data?.detail || err?.message || 'request failed';
      try { toast.error(`Could not update signup-OTP setting — ${detail}`); } catch {}
    } finally {
      setBusy(false);
    }
  };
  if (collapsed) {
    return (
      <div className="mx-1 my-2 flex items-center justify-center px-2 py-2 rounded-lg cursor-pointer" onClick={toggle} title={`Signup OTP ${signupOtpDisabled ? 'Disabled' : 'Enabled'}`} style={{ background: signupOtpDisabled ? 'rgba(239,68,68,0.06)' : 'var(--s)', border: `1px solid ${signupOtpDisabled ? 'rgba(239,68,68,0.2)' : 'var(--b)'}` }}>
        <ShieldCheck className="w-5 h-5" style={{ color: signupOtpDisabled ? '#ef4444' : '#10b981' }} />
      </div>
    );
  }
  return (
    <div className="mx-3 my-2 flex items-center justify-between px-3 py-2 rounded-lg" style={{ background: signupOtpDisabled ? 'rgba(239,68,68,0.06)' : 'var(--s)', border: `1px solid ${signupOtpDisabled ? 'rgba(239,68,68,0.2)' : 'var(--b)'}` }}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" style={{ color: signupOtpDisabled ? '#ef4444' : '#10b981' }} />
        <span className="text-xs font-bold text-[var(--t)]">Signup OTP</span>
      </div>
      <Switch checked={!signupOtpDisabled} onCheckedChange={toggle} data-testid="sidebar-signup-otp-toggle" />
    </div>
  );
};

/**
 * OfflineModeToggle — founder-only switch to flip the platform-wide offline
 * feature on/off.
 *
 * Persistence model (Feb 2026 update): the AUTHORITATIVE store is the
 * MongoDB `platform_settings.offline_mode` document, exposed via the
 * existing `/api/admin/platform-settings` GET/PUT pair (same surface
 * SignupOtpToggle and OtpToggle use). On mount we hydrate from the
 * server, fall back to whatever localStorage already had if the
 * request fails (so a fresh PWA install / new deploy resumes the
 * founder's last-set value automatically), and on toggle we PUT to
 * the server BEFORE touching localStorage.
 *
 * Why this matters: deleting the PWA from the home screen and pushing
 * a new deployment both wipe the per-device localStorage on iOS, so
 * the previous "localStorage-only" persistence reset to 'off' on
 * every reinstall — forcing the founder to re-flip the toggle before
 * every user-side regression check. The server flag means one-time
 * flip, sticks forever.
 *
 * The localStorage key (`carryon_offline_v1`) is still the runtime
 * truth for synchronous reads inside `featureFlag.js` — we keep both
 * in sync so the rest of the offline subsystem doesn't have to await
 * a network round-trip on every isOfflineEnabled() call.
 */
const OfflineModeToggle = ({ collapsed }) => {
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem('carryon_offline_v1') === 'on'; }
    catch { return false; }
  });

  // On mount, hydrate from the authoritative server flag so a fresh
  // PWA install / new deploy automatically restores the founder's last
  // chosen value instead of resetting to 'off'.
  useEffect(() => {
    const token = (() => { try { return localStorage.getItem('carryon_token'); } catch { return null; } })();
    if (!token) return;
    let cancelled = false;
    apiClient.get(`${API_URL}/admin/platform-settings`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (cancelled) return;
      const serverMode = res?.data?.offline_mode;
      if (serverMode !== 'on' && serverMode !== 'off') return; // never set yet
      const serverOn = serverMode === 'on';
      try {
        const localVal = localStorage.getItem('carryon_offline_v1');
        const localOn = localVal === 'on';
        if (serverOn !== localOn) {
          // Server disagrees with this device — server wins. Hydrate
          // localStorage and the toggle UI to match. We deliberately
          // do NOT reload here: that would surprise the founder mid-
          // session every time they open the sidebar. The next page
          // load (or an explicit toggle click) will pick up the new
          // value via the existing reload path.
          localStorage.setItem('carryon_offline_v1', serverOn ? 'on' : 'off');
          setOn(serverOn);
          try { window.dispatchEvent(new CustomEvent('carryon:offline-flag-changed', { detail: { mode: serverOn ? 'on' : 'off' } })); } catch {}
        }
      } catch {}
    }).catch(() => { /* offline / 401 — keep local default */ });
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    const next = !on;
    setOn(next);
    const token = (() => { try { return localStorage.getItem('carryon_token'); } catch { return null; } })();
    // Persist to the server FIRST so the choice survives PWA reinstalls
    // and new deployments. Best-effort: a 4xx/5xx still updates the
    // local toggle so the founder's current session isn't blocked, but
    // the value won't survive the next reload — that's a strictly
    // better failure mode than silently dropping the click.
    if (token) {
      try {
        await apiClient.put(
          `${API_URL}/admin/platform-settings`,
          { offline_mode: next ? 'on' : 'off' },
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
        );
      } catch { /* non-fatal — local flag still toggles below */ }
    }
    try {
      localStorage.setItem('carryon_offline_v1', next ? 'on' : 'off');
    } catch {}
    // Mirror the platform-visibility flag locally so every other
    // component (Settings cards, onboarding tile) re-renders without
    // waiting for the next /public/site-content boot fetch.
    try {
      const { setPlatformOfflineFlag } = await import('../../utils/platformOfflineFlag');
      setPlatformOfflineFlag(next ? 'on' : 'off');
    } catch {}
    // Broadcast so any listening components update without a reload.
    try { window.dispatchEvent(new CustomEvent('carryon:offline-flag-changed', { detail: { mode: next ? 'on' : 'off' } })); } catch {}
    // Reload so repos, service worker, and crypto session key reinitialize cleanly.
    // Use a short delay so React state flush completes first.
    setTimeout(() => { try { window.location.reload(); } catch {} }, 150);
  };
  if (collapsed) {
    return (
      <div
        className="mx-1 my-2 flex items-center justify-center px-2 py-2 rounded-lg cursor-pointer"
        onClick={toggle}
        title={`Offline mode ${on ? 'On' : 'Off'}`}
        style={{ background: on ? 'rgba(var(--gold-rgb), 0.10)' : 'var(--s)', border: `1px solid ${on ? 'rgba(var(--gold-rgb), 0.35)' : 'var(--b)'}` }}
        data-testid="sidebar-offline-toggle-collapsed"
      >
        <CloudOff className="w-5 h-5" style={{ color: on ? '#d4af37' : 'var(--t3)' }} />
      </div>
    );
  }
  return (
    <div
      className="mx-3 my-2 flex items-center justify-between px-3 py-2 rounded-lg"
      style={{ background: on ? 'rgba(var(--gold-rgb), 0.10)' : 'var(--s)', border: `1px solid ${on ? 'rgba(var(--gold-rgb), 0.35)' : 'var(--b)'}` }}
      data-testid="sidebar-offline-toggle"
    >
      <div className="flex items-center gap-2">
        <CloudOff className="w-4 h-4" style={{ color: on ? '#d4af37' : 'var(--t3)' }} />
        <span className="text-xs font-bold text-[var(--t)]">Offline</span>
      </div>
      <Switch checked={on} onCheckedChange={toggle} data-testid="sidebar-offline-switch" />
    </div>
  );
};

// Normalize admin_scope to an array regardless of input format
const scopeArr = (raw) => Array.isArray(raw) ? raw : (raw ? [raw] : []);
const _hasScope = (raw, target) => scopeArr(raw).includes(target);

const ADMIN_PORTALS = [
  { scope: 'founder', label: 'Founder Portal', color: '#d4af37' },
  { scope: 'ops_manager', label: 'Operations Portal', color: '#E87040', altScope: 'ops_team' },
  { scope: 'finance', label: 'Finance Portal', color: '#22C993' },
  { scope: 'compliance', label: 'Compliance Portal', color: '#3B82F6' },
  { scope: 'marketing', label: 'Marketing Portal', color: '#B794F6' },
  { scope: 'platform_health', label: 'Platform Portal', color: '#F59E0B' },
];

const Sidebar = () => {
  const { user, logout, refreshUser, enabledFeatures, setUser: _setUser, partnerBranding } = useAuth();
  const cleanLabel = useLabelCleaner();
  // Brand for partner-co-branded labels (CFP/CCP/Core Pillars etc.).
  // Legal text, footers, ™ marks and "powered by" lines stay as CarryOn.
  const brand = partnerBranding?.companyName || 'CarryOn';
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [benEstates, setBenEstates] = useState([]);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('carryon_sidebar_collapsed') === 'true');
  const [estatePickerOpen, setEstatePickerOpen] = useState(false);
  const [ectUnread, setEctUnread] = useState(0);
  // User's custom menu order (from /api/user-preferences/menu-order).
  // This is a cosmetic reorder overlay applied AFTER tier gating.
  // Keyed by role so benefactor-context and beneficiary-context orders
  // persist independently.
  const [menuOrderBenefactor, setMenuOrderBenefactor] = useState([]);
  const [menuOrderBeneficiary, setMenuOrderBeneficiary] = useState([]);
  // Dev portal switcher (founder only)
  const [devOpen, setDevOpen] = useState(false);
  const [devConfig, setDevConfig] = useState(() => {
    try {
      const cached = sessionStorage.getItem('dev_switcher_config');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [devSwitching, setDevSwitching] = useState(null);
  // Platform-wide Subscription visibility — when the founder turns the
  // admin "Subscriptions" toggle off, the menu item disappears for everyone.
  const [subsVisible, setSubsVisible] = useState(() => isPlatformSubscriptionsVisible());
  useEffect(() => {
    const onChange = () => setSubsVisible(isPlatformSubscriptionsVisible());
    window.addEventListener(PLATFORM_SUBSCRIPTIONS_FLAG_EVENT, onChange);
    return () => window.removeEventListener(PLATFORM_SUBSCRIPTIONS_FLAG_EVENT, onChange);
  }, []);

  const isAdminSession = user?.role === 'admin' || localStorage.getItem('dev_switcher_admin_session') === 'true';

  const fetchDevConfig = async () => {
    try {
      // /dev-switcher/config is mounted behind require_scope("founder"), so the
      // request MUST carry the admin token. In a switched (benefactor/beneficiary)
      // session the original founder token is preserved in dev_switcher_admin_token.
      const adminToken = localStorage.getItem('dev_switcher_admin_token') || localStorage.getItem('carryon_token');
      const res = await fetch(`${BASE_URL}/api/dev-switcher/config`, {
        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
      });
      if (!res.ok) return;
      const data = await res.json();
      if (adminToken) {
        try {
          const opsRes = await fetch(`${BASE_URL}/api/founder/operators`, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          if (opsRes.ok) {
            data.operators = await opsRes.json();
          }
        } catch {}
      }
      setDevConfig(data);
      try { sessionStorage.setItem('dev_switcher_config', JSON.stringify(data)); } catch {}
    } catch {}
  };

  useEffect(() => {
    if (isAdminSession) fetchDevConfig();
  }, [isAdminSession]); // eslint-disable-line

  const devAccounts = [];
  if (devConfig?.benefactor?.email) devAccounts.push({ label: 'Benefactor', email: devConfig.benefactor.email, password: devConfig.benefactor.password, role: 'benefactor', color: '#2563eb', redirect: '/dashboard' });
  if (devConfig?.beneficiary?.email) devAccounts.push({ label: 'Beneficiary', email: devConfig.beneficiary.email, password: devConfig.beneficiary.password, role: 'beneficiary', color: '#8b5cf6', redirect: '/beneficiary' });
  devAccounts.push({ label: 'Founder Portal', role: 'admin', color: '#E0AD2B', redirect: '/admin' });
  devAccounts.push({ label: 'Operations Portal', role: 'ops_view', color: '#3B82F6', redirect: '/ops' });
  // Add operator accounts from the operators list
  if (devConfig?.operators) {
    devConfig.operators.forEach(op => {
      const isManager = op.operator_role === 'manager';
      devAccounts.push({
        label: `${op.name} (${isManager ? 'Manager' : 'Team Member'})`,
        email: op.email,
        role: `operator_${op.id}`,
        color: isManager ? '#F59E0B' : '#06B6D4',
        redirect: '/ops',
        isOperator: true,
      });
    });
  }

  const SCOPE_PREVIEWS = [
    { scope: 'finance', label: 'Finance Admin', desc: 'Revenue, Subs, Grace Periods', color: '#22C993' },
    { scope: 'compliance', label: 'Compliance Admin', desc: 'Audit, Security, Estate Health', color: '#3B82F6' },
    { scope: 'marketing', label: 'Marketing Admin', desc: 'Funnel, Beta, Site Content, Emails', color: '#B794F6' },
    { scope: 'platform_health', label: 'Platform Health', desc: 'System, Operators, Integrations', color: '#F59E0B' },
  ];

  // Check portal visibility from dev config (default: visible)
  const pv = devConfig?.portal_visibility || {};
  const isPortalOn = (key) => pv[key] !== false;

  const visibleScopePreviews = SCOPE_PREVIEWS.filter(sp => isPortalOn(sp.scope));

  const handleScopePreview = (scope) => {
    setDevOpen(false);
    toast.success(`Viewing as: ${SCOPE_PREVIEWS.find(s => s.scope === scope)?.label || scope}`);
    navigate(`/admin?scope=${scope}`);
  };

  const handleRestoreFounder = () => {
    setDevOpen(false);
    toast.success('Restored Founder view');
    navigate('/admin');
  };

  const handleDevSwitch = async (account) => {
    // Portal switching mints a fresh server-side JWT, so it genuinely
    // cannot work offline. Surface a clean explanation instead of the
    // raw "Load failed" network error.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      toast.error('Portal switching requires an internet connection. Reconnect and try again.');
      return;
    }
    setDevSwitching(account.role);
    try {
      const currentToken = localStorage.getItem('carryon_token');
      if (user?.role === 'admin' && currentToken) {
        localStorage.setItem('dev_switcher_admin_token', currentToken);
      }
      if (account.role === 'admin' || account.role === 'ops_view') {
        const adminToken = localStorage.getItem('dev_switcher_admin_token');
        if (adminToken) {
          localStorage.setItem('carryon_token', adminToken);
          localStorage.removeItem('selected_estate_id');
          localStorage.removeItem('beneficiary_estate_id');
          localStorage.removeItem('beneficiary_feature_access');
          localStorage.setItem('dev_switcher_admin_session', 'true');
          localStorage.setItem('dev_switcher_active_role', account.role);
          window.location.href = account.redirect;
          return;
        }
        throw new Error('No admin session found.');
      }
      // Operator accounts — use admin impersonation via dev-login
      if (account.isOperator) {
        const adminToken = localStorage.getItem('dev_switcher_admin_token') || currentToken;
        if (!adminToken) throw new Error('No admin session found.');
        // Fetch the operator's password from the backend (admin-only endpoint)
        const loginRes = await fetch(`${BASE_URL}/api/founder/operator-dev-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
          body: JSON.stringify({ operator_email: account.email }),
        });
        let loginData;
        try { loginData = await loginRes.json(); } catch { loginData = { detail: `Server returned ${loginRes.status}` }; }
        if (!loginRes.ok) throw new Error(loginData.detail || 'Login failed');
        localStorage.removeItem('selected_estate_id');
        localStorage.removeItem('beneficiary_estate_id');
        localStorage.setItem('dev_switcher_admin_session', 'true');
        localStorage.setItem('dev_switcher_active_role', account.role);
        localStorage.setItem('carryon_token', loginData.access_token);
        window.location.href = account.redirect;
        return;
      }
      const switchToken = localStorage.getItem('dev_switcher_admin_token') || currentToken;
      if (!switchToken) throw new Error('No active session.');
      const response = await fetch(`${BASE_URL}/api/auth/dev-switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${switchToken}` },
        body: JSON.stringify({ email: account.email }),
      });
      let data;
      try { data = await response.json(); } catch { data = { detail: `Server returned ${response.status}` }; }
      if (!response.ok) throw new Error(data.detail || 'Login failed');
      localStorage.removeItem('selected_estate_id');
      localStorage.removeItem('beneficiary_estate_id');
      localStorage.setItem('dev_switcher_admin_session', 'true');
      localStorage.setItem('dev_switcher_active_role', account.role);
      localStorage.setItem('carryon_token', data.access_token);
      window.location.href = account.redirect;
    } catch (err) {
      toast.error('Switch failed: ' + err.message);
      setDevSwitching(null);
    }
  };

  const toggleCollapsed = () => {
    haptics.light();
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('carryon_sidebar_collapsed', String(next));
    window.dispatchEvent(new Event('sidebar-toggle'));
  };

  // Fetch estates for sidebar switcher (beneficiary view + multi-role users)
  useEffect(() => {
    if (user?.role === 'beneficiary' || user?.is_also_beneficiary || user?.is_also_benefactor) {
      // Offline rescue first — show every cached estate the user is
      // connected to so the switcher works without a connection.
      try {
        const cached = JSON.parse(localStorage.getItem('carryon_list_cache:beneficiary:estates') || 'null');
        if (Array.isArray(cached?.value)) setBenEstates(cached.value);
      } catch { /* ignore */ }

      const token = localStorage.getItem('carryon_token');
      if (token && (typeof navigator === 'undefined' || navigator.onLine !== false)) {
        apiClient.get(`${API_URL}/estates`, { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            setBenEstates(res.data);
            // Persist for offline use under the same key
            // beneficiaryOfflineCache writes to.
            try {
              localStorage.setItem(
                'carryon_list_cache:beneficiary:estates',
                JSON.stringify({ value: res.data || [], ts: Date.now() })
              );
            } catch { /* quota — non-fatal */ }
          })
          .catch(() => {});
      }
    }
  }, [user]);

  // Poll ECT unread count for badge
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') return;
    const fetchUnread = () => {
      const tk = localStorage.getItem('carryon_token');
      if (!tk) return;
      fetch(`${BASE_URL}/api/estate-chat/unread-total`, { headers: { Authorization: `Bearer ${tk}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setEctUnread(d.total || 0); })
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Load the user's saved menu order (cosmetic reorder overlay on top of
  // the tier-gated feature list). Only benefactor + beneficiary roles have
  // a reorder-able feature section — staff portals are workflow tools.
  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'operator') return;
    const tk = localStorage.getItem('carryon_token');
    if (!tk) return;
    const roles = ['benefactor', 'beneficiary'];
    Promise.all(roles.map((role) =>
      fetch(`${BASE_URL}/api/user-preferences/menu-order?role=${role}`, { headers: { Authorization: `Bearer ${tk}` } })
        .then((r) => (r.ok ? r.json() : { items: [] }))
        .catch(() => ({ items: [] }))
    )).then(([ben, bny]) => {
      setMenuOrderBenefactor(Array.isArray(ben?.items) ? ben.items : []);
      setMenuOrderBeneficiary(Array.isArray(bny?.items) ? bny.items : []);
    });
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation structure matching prototype. Order + labels per user spec:
  // Dashboard → Beneficiaries → MM → SDV → EGA → CFP → CCP → ECT → IAC →
  // DTS → FFN → DAV → EPT. FFN / DAV / EPT live at the bottom as the
  // 'post-transition' trio. Labels are "Full Name (ACRONYM)" everywhere
  // so users learn the shorthand that's used throughout the platform.
  const benefactorNavSections = [
    {
      title: 'ESTATE PLAN ACCESS',
      items: applyUserMenuOrder(filterNavByFeatures([
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/beneficiaries', icon: Users, label: 'Beneficiaries' },
        { to: '/messages', icon: MessageSquare, label: 'Milestone Messages (MM)' },
        { to: '/vault', icon: FolderLock, label: 'Secure Document Vault (SDV)' },
        { to: '/checklist', icon: CheckSquare, label: 'Immediate Action Checklist (IAC)' },
        { to: '/guardian', icon: Sparkles, label: 'Estate Guardian AI (EGA)' },
        { to: '/financial', icon: DollarSign, label: joinBrandSuffix(brand, 'Financial Picture (CFP)') },
        { to: '/digital-wallet', icon: KeyRound, label: 'Digital Access Vault (DAV)' },
        { to: '/ffn', icon: Heart, label: 'Friends & Family Notification (FFN)' },
        { to: '/connected-protocol', icon: Shield, label: `${brand} Contingency Protocols (CCP)` },
        { to: '/estate-chat', icon: MessageCircle, label: 'Estate Comms Tool (ECT)', badge: ectUnread },
        { to: '/trustee', icon: Shield, label: 'Designated Trustee Services (DTS)' },
        { to: '/timeline', icon: Clock, label: 'Estate Plan Timeline (EPT)' },
      ], enabledFeatures), menuOrderBenefactor)
    },
    {
      title: 'ACCOUNT',
      items: [
        { to: '/settings', icon: Settings, label: 'Settings' },
        ...(subsVisible ? [{ to: '/subscription', icon: CreditCard, label: 'Subscription' }] : []),
        { to: '/security-settings', icon: ShieldCheck, label: 'Security Settings' },
        { to: '/support', icon: Headphones, label: 'Customer Support' },
      ]
    }
  ];

  // Get feature access flags from localStorage (set by TransitionGate/Dashboard)
  const featureAccess = (() => {
    try { return JSON.parse(localStorage.getItem('beneficiary_feature_access') || '{}'); }
    catch { return {}; }
  })();

  // Map nav routes to feature access flags
  const NAV_FEATURE_MAP = {
    '/beneficiary/vault': 'sdv_access',
    '/beneficiary/concierge': 'bec_access',
    '/beneficiary/checklist': 'iac_access',
    '/beneficiary/messages': 'mm_access',
    '/beneficiary/financial': 'cfp_access',
  };

  const filterByFeatureAccess = (items) =>
    items.filter(item => {
      const flag = NAV_FEATURE_MAP[item.to];
      return !flag || featureAccess[flag] !== false;
    });

  const beneficiaryNavSections = [
    {
      title: 'ESTATE PLAN ACCESS',
      items: applyUserMenuOrder(filterNavByFeatures(filterByFeatureAccess([
        { to: '/beneficiary', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/beneficiary/messages', icon: MessageSquare, label: 'Milestone Messages (MM)' },
        { to: '/beneficiary/vault', icon: FolderLock, label: 'Secure Document Vault (SDV)' },
        { to: '/beneficiary/checklist', icon: CheckSquare, label: 'Immediate Action Checklist (IAC)' },
        { to: '/beneficiary/concierge', icon: BookOpen, label: 'Beneficiary Estate Concierge (BEC)' },
        { to: '/beneficiary/financial', icon: DollarSign, label: joinBrandSuffix(brand, 'Financial Picture (CFP)') },
        { to: '/beneficiary/connected-protocol', icon: Shield, label: `${brand} Contingency Protocols (CCP)` },
        { to: '/beneficiary/estate-chat', icon: MessageCircle, label: 'Estate Comms Tool (ECT)', badge: ectUnread },
        { to: '/beneficiary/milestone', icon: Home, label: 'Report Milestone' },
      ]), enabledFeatures), menuOrderBeneficiary)
    },
    {
      title: 'ACCOUNT',
      items: [
        { to: '/beneficiary/settings', icon: Settings, label: 'Settings' },
        ...(subsVisible ? [{ to: '/beneficiary/subscription', icon: CreditCard, label: 'Subscription' }] : []),
        { to: '/support', icon: Headphones, label: 'Customer Support' },
      ]
    }
  ];

  const adminNavSections = [
    {
      title: 'TOOLS',
      items: [
        { to: '/admin/announcements', icon: Megaphone, label: 'Announcements' },
        { to: '/admin/system-health', icon: HeartPulse, label: 'System Health' },
        { to: '/admin/escalations', icon: AlertTriangle, label: 'Escalations' },
        { to: '/admin/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
        { to: '/admin/p1-settings', icon: Shield, label: 'P1 Contact Settings' },
        { to: '/settings', icon: Settings, label: 'Settings' },
      ]
    }
  ];

  const operatorNavSections = [
    {
      title: 'TOOLS',
      items: [
        // Managers get dashboard first
        ...(user?.operator_role === 'manager' ? [
          { to: '/ops/dashboard', icon: LayoutDashboard, label: 'Ops Dashboard' },
          { to: '/ops/operators', icon: Users, label: 'Team' },
        ] : []),
        { to: '/ops/my-activity', icon: Clock, label: 'My Activity' },
        { to: '/ops/search', icon: Search, label: 'Quick Search' },
        { to: '/ops/escalations', icon: AlertTriangle, label: 'Escalate' },
        { to: '/ops/shift-notes', icon: StickyNote, label: 'Shift Notes' },
        { to: '/ops/milestones', icon: Gift, label: 'Milestones' },
        { to: '/ops/users', icon: Users, label: 'Users' },
        ...(user?.operator_role === 'manager' ? [
          { to: '/ops/subscriptions', icon: CreditCard, label: 'Subs' },
        ] : []),
        { to: '/ops/system-health', icon: HeartPulse, label: 'System Health' },
        { to: '/ops/estate-health', icon: HeartPulse, label: 'Estate Health' },
        { to: '/ops/knowledge-base', icon: BookOpen, label: 'SOPs' },
        { to: '/settings', icon: Settings, label: 'Settings' },
      ]
    }
  ];

  // Determine if the user is currently viewing in a "benefactor context"
  // (a beneficiary who also owns an estate, viewing their estate dashboard)
  const isBenefactorContext = (() => {
    const path = window.location.pathname;
    // If user is a beneficiary with is_also_benefactor, check current path context
    if (user?.role === 'beneficiary' && user?.is_also_benefactor) {
      // On benefactor routes = benefactor context
      return !path.startsWith('/beneficiary');
    }
    return false;
  })();

  const getNavSections = () => {
    // Admin viewing ops portal should see operator nav
    let sections;
    if (user?.role === 'admin' && window.location.pathname.startsWith('/ops')) sections = operatorNavSections;
    else if (user?.role === 'admin') sections = adminNavSections;
    else if (user?.role === 'operator') sections = operatorNavSections;
    // Multi-role: beneficiary who is also a benefactor
    else if (user?.role === 'beneficiary' && isBenefactorContext) sections = benefactorNavSections;
    else if (user?.role === 'beneficiary') sections = beneficiaryNavSections;
    // Benefactor who is also a beneficiary — check path
    else if (user?.role === 'benefactor' && window.location.pathname.startsWith('/beneficiary')) sections = beneficiaryNavSections;
    else sections = benefactorNavSections;
    // Strip parenthetical feature acronyms for B2B partner sessions.
    // No-op for direct consumers (cleanLabel returns text unchanged).
    if (partnerBranding?.companyName) {
      return sections.map(sec => ({
        ...sec,
        items: sec.items?.map(it => ({ ...it, label: cleanLabel(it.label) })),
      }));
    }
    return sections;
  };

  const getRoleLabel = () => {
    if (user?.role === 'beneficiary' && isBenefactorContext) return 'BENEFACTOR PORTAL';
    if (user?.role === 'beneficiary') return 'BENEFICIARY PORTAL';
    if (user?.role === 'admin' && window.location.pathname.startsWith('/ops')) return 'OPERATIONS';
    if (user?.role === 'admin') {
      const raw = user?.admin_scope || 'founder';
      const scopes = Array.isArray(raw) ? raw : [raw];
      if (scopes.includes('founder')) return 'FOUNDER PORTAL';
      const labels = { finance: 'FINANCE', compliance: 'COMPLIANCE', marketing: 'MARKETING', platform_health: 'PLATFORM' };
      return scopes.map(s => labels[s] || s.toUpperCase()).join(' + ') + ' ADMIN';
    }
    if (user?.role === 'operator' && user?.operator_role === 'manager') return 'OPS MANAGER';
    if (user?.role === 'operator') return 'OPERATIONS';
    if (user?.role === 'benefactor' && window.location.pathname.startsWith('/beneficiary')) return 'BENEFICIARY PORTAL';
    return 'BENEFACTOR PORTAL';
  };

  return (
    <aside className={`sb hidden lg:flex ${collapsed ? 'collapsed' : ''}`} data-testid="sidebar" role="navigation" aria-label="Main navigation">
      {/* Logo Section — clickable for founder portal switcher */}
      <div className="sb-logo" style={{ cursor: 'pointer' }}
        onClick={() => { if (isAdminSession) { setDevOpen(!devOpen); if (!devOpen) fetchDevConfig(); } else { navigate(window.location.pathname.startsWith('/beneficiary') ? '/beneficiary' : '/dashboard'); } }}
        data-testid="sidebar-logo">
        <div className="sb-logo-top">
          <img 
            src={partnerBranding?.logoUrl || "/carryon-app-icon.png"} 
            alt={partnerBranding?.companyName || "CarryOn™"} 
            className="sb-logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div 
            className="sb-avatar" 
            style={{ display: 'none', width: '42px', height: '42px', borderRadius: '10px' }}
          >
            <Shield className="w-5 h-5" />
          </div>
          {!collapsed && (() => {
            const _brand = partnerBranding?.companyName || 'CarryOn™';
            // Scale the title down for long brand names so they fit
            // the sidebar width without ugly multi-line wraps.
            //   ≤14 chars → 28px (default)
            //   15-22     → 22px
            //   23-32     → 18px
            //   >32       → 15px
            let _fs = 28;
            if (_brand.length > 32) _fs = 15;
            else if (_brand.length > 22) _fs = 18;
            else if (_brand.length > 14) _fs = 22;
            return (
              <span
                className="sb-logo-title"
                style={{ fontSize: _fs, lineHeight: 1.15, wordBreak: 'break-word' }}
              >
                {_brand}
              </span>
            );
          })()}
        </div>
        {!collapsed && <span className="sb-logo-subtitle" data-testid="sidebar-portal-label">{getRoleLabel()}</span>}
      </div>

      {/* Dev Portal Switcher Panel — founder only, floating overlay */}
      {devOpen && isAdminSession && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
            onClick={() => setDevOpen(false)} />
          <div style={{
            position: 'fixed', top: 70, left: 8, width: 280, maxHeight: 'calc(100vh - 100px)',
            background: '#0F1629', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 100,
            overflowY: 'auto',
          }}>
            <div style={{ padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 8 }}>
                Portal Switcher
              </div>
              {user && (
                <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 8 }}>
                  Logged in as: <strong style={{ color: '#E2E8F0' }}>{user.name || user.email}</strong>
                  <br /><span style={{ textTransform: 'capitalize', color: '#F0C95C' }}>{user.role}</span>
                </div>
              )}
              {!devConfig?.benefactor?.email && !devConfig?.beneficiary?.email && (
                <div style={{ padding: 10, background: 'rgba(245,158,11,0.1)', borderRadius: 8, marginBottom: 8, border: '1px dashed rgba(245,158,11,0.3)' }}>
                  <div style={{ fontSize: 11, color: '#F59E0B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Settings className="w-3 h-3" /> Not Configured
                  </div>
                  <div style={{ fontSize: 11, color: '#94A3B8' }}>
                    Go to Admin → Dev Switcher to assign accounts for quick switching.
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {devAccounts.filter(a => !a.isOperator && (a.role === 'admin' || a.role === 'ops_view' ? isPortalOn(a.role === 'ops_view' ? 'operations' : 'founder') : isPortalOn(a.role))).map(acc => {
                  const devActiveRole = localStorage.getItem('dev_switcher_active_role');
                  const isActive = acc.role === 'admin'
                    ? user?.role === 'admin' && (!devActiveRole || devActiveRole === 'admin')
                    : acc.role === 'ops_view'
                      ? devActiveRole === 'ops_view'
                      : devActiveRole
                        ? acc.role === devActiveRole && user?.email === acc.email
                        : acc.role === user?.role && user?.email === acc.email;
                  return (
                    <div key={acc.role}
                      onClick={(e) => { e.stopPropagation(); if (!isActive && !devSwitching) handleDevSwitch(acc); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        background: isActive ? 'rgba(224,173,43,0.1)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isActive ? 'rgba(224,173,43,0.3)' : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 10, cursor: isActive || devSwitching ? 'default' : 'pointer',
                        transition: 'all .15s', opacity: devSwitching ? 0.5 : 1,
                      }}
                      data-testid={`dev-switch-${acc.role}`}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', background: acc.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0,
                        border: isActive ? '2px solid #F0C95C' : '2px solid transparent',
                      }}>
                        {acc.role[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? '#F0C95C' : '#E2E8F0' }}>{acc.label}</div>
                        <div style={{ fontSize: 11, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.email || (acc.role === 'admin' ? 'Restore admin session' : acc.role === 'ops_view' ? 'View as operator' : 'Not configured')}</div>
                      </div>
                      {isActive && <span style={{ fontSize: 11, color: '#F0C95C', flexShrink: 0 }}>Active</span>}
                      {devSwitching === acc.role && <div className="w-4 h-4 border-2 border-[#F0C95C] border-t-transparent rounded-full animate-spin" />}
                    </div>
                  );
                })}
              </div>
              {/* Scope Preview — admin only, shown right after main portals */}
              {user?.role === 'admin' && visibleScopePreviews.length > 0 && (
                <div style={{ marginTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6, paddingLeft: 2 }}>
                    Admin Scope Preview
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {new URLSearchParams(window.location.search).get('scope') && (
                      <div
                        onClick={(e) => { e.stopPropagation(); handleRestoreFounder(); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          background: 'rgba(var(--gold-rgb), 0.08)', border: '1px solid rgba(var(--gold-rgb), 0.25)',
                          borderRadius: 8, cursor: 'pointer', transition: 'all .15s',
                        }}
                        data-testid="scope-restore-founder"
                      >
                        <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#d4af37', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#0F1629' }}>F</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#d4af37' }}>Restore Founder View</div>
                      </div>
                    )}
                    {visibleScopePreviews.map(sp => {
                      const activeScopeParam = new URLSearchParams(window.location.search).get('scope');
                      const isActive = activeScopeParam === sp.scope;
                      return (
                        <div key={sp.scope}
                          onClick={(e) => { e.stopPropagation(); if (!isActive) handleScopePreview(sp.scope); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                            background: isActive ? `${sp.color}12` : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${isActive ? `${sp.color}40` : 'rgba(255,255,255,0.04)'}`,
                            borderRadius: 8, cursor: isActive ? 'default' : 'pointer', transition: 'all .15s',
                          }}
                          data-testid={`scope-${sp.scope}`}
                        >
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: sp.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white' }}>{sp.label[0]}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: isActive ? sp.color : '#CBD5E1' }}>{sp.label}</div>
                            <div style={{ fontSize: 11, color: '#525C72' }}>{sp.desc}</div>
                          </div>
                          {isActive && <span style={{ fontSize: 11, color: sp.color, flexShrink: 0 }}>Active</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Operator accounts — at the bottom */}
              {devAccounts.filter(a => a.isOperator).length > 0 && (
                <div style={{ marginTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6, paddingLeft: 2 }}>
                    Operator Accounts
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {devAccounts.filter(a => a.isOperator).map(acc => {
                      const devActiveRole = localStorage.getItem('dev_switcher_active_role');
                      const isActive = devActiveRole ? acc.role === devActiveRole && user?.email === acc.email : false;
                      return (
                        <div key={acc.role}
                          onClick={(e) => { e.stopPropagation(); if (!isActive && !devSwitching) handleDevSwitch(acc); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                            background: isActive ? 'rgba(224,173,43,0.1)' : 'rgba(255,255,255,0.02)',
                            border: `1px solid ${isActive ? 'rgba(224,173,43,0.3)' : 'rgba(255,255,255,0.04)'}`,
                            borderRadius: 8, cursor: isActive || devSwitching ? 'default' : 'pointer',
                            transition: 'all .15s', opacity: devSwitching ? 0.5 : 1,
                          }}
                          data-testid={`dev-switch-${acc.role}`}>
                          <div style={{
                            width: 22, height: 22, borderRadius: '50%', background: acc.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0,
                          }}>
                            {acc.label[0]}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: isActive ? '#F0C95C' : '#CBD5E1', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.label}</div>
                          </div>
                          {devSwitching === acc.role && <div className="w-3 h-3 border-2 border-[#F0C95C] border-t-transparent rounded-full animate-spin" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 11, color: '#525C72', textAlign: 'center' }}>No OTP required · Instant switch</div>
            </div>
          </div>
        </>
      )}

      {/* Admin OTP Toggle — Founder only, not operators */}
      {user?.role === 'admin' && !window.location.pathname.startsWith('/ops') && (
        <div className="flex flex-col gap-2 [&>*]:my-0">
          <OtpToggle collapsed={collapsed} />
          <SignupOtpToggle collapsed={collapsed} />
          {/* Offline mode master switch — founder-only. Placed directly
              below the OTP toggle per PM request. Single knob engages
              IndexedDB sync, outbox drain, pending uploads, and at-rest
              encryption together. */}
          <OfflineModeToggle collapsed={collapsed} />
          <PlatformBooleanToggle
            settingKey="platform_free_mode"
            label="Free Mode"
            Icon={Gift}
            activeColor="#22c55e"
            activeLabel="On"
            inactiveLabel="Off"
            collapsed={collapsed}
            testId="sidebar-free-mode-toggle"
            activeBadge="LIVE"
          />
          {/* Public live counters (homepage "N families set up" badge, trust-block numbers).
              One switch, default OFF — nothing on the public site shows live metrics until it's ON. */}
          <PlatformBooleanToggle
            settingKey="live_metrics_enabled"
            label="Live Metrics"
            Icon={Activity}
            activeColor="#10b981"
            activeLabel="Shown"
            inactiveLabel="Hidden"
            collapsed={collapsed}
            testId="sidebar-live-metrics-toggle"
          />
          <PlatformBooleanToggle
            settingKey="ai_burn_guard_enabled"
            label="AI Guard"
            Icon={Sparkles}
            activeColor="#f59e0b"
            activeLabel="On"
            inactiveLabel="Off"
            collapsed={collapsed}
            testId="sidebar-ai-burn-guard-toggle"
          />
          {/* Subscription page master switch — when OFF, the Subscription
              menu item is hidden for EVERY user platform-wide. */}
          <PlatformBooleanToggle
            settingKey="subscriptions_enabled"
            label="Subscriptions"
            Icon={CreditCard}
            activeColor="#3b82f6"
            activeLabel="Shown"
            inactiveLabel="Hidden"
            collapsed={collapsed}
            testId="sidebar-subscriptions-toggle"
            onChange={(val) => setPlatformSubscriptionsFlag(val ? 'on' : 'off')}
          />
        </div>
      )}

      {/* Beta Banner — only shows when beta_mode is active */}
      <BetaBanner collapsed={collapsed} />

      {/* Navigation Sections */}
      <nav className="flex-1 overflow-y-auto py-4" style={{ overscrollBehavior: 'contain' }}>
        {/* Admin Portal — render the expandable Section → Tab menu
            instead of the legacy flat TOOLS list. (Operator view at
            /ops still uses the legacy flat list below.) */}
        {user?.role === 'admin' && !window.location.pathname.startsWith('/ops') ? (
          <AdminSectionNav
            collapsed={collapsed}
            variant="sidebar"
            adminScopes={scopeArr(user?.admin_scope).length > 0 ? scopeArr(user?.admin_scope) : ['founder']}
          />
        ) : (() => {
          // Benefactor portal — render the 4-section expandable menu.
          // Beneficiary side keeps the legacy flat list for now (per
          // user instruction May 22 2026: "just benefactor side for
          // now"). Multi-role users acting in benefactor context also
          // see the new section nav.
          const onBeneficiarySide = window.location.pathname.startsWith('/beneficiary');
          const isBenefactorView =
            (user?.role === 'benefactor' && !onBeneficiarySide) ||
            (user?.role === 'beneficiary' && user?.is_also_benefactor && !onBeneficiarySide);
          if (isBenefactorView) {
            return (
              <>
                {/* Dashboard pinned ABOVE the sections (mirrors how
                    the admin Founder Dashboard sits above the admin
                    section nav). */}
                <div className="nav-section">
                  <NavLink
                    to="/dashboard"
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                    data-testid="nav-dashboard"
                    title={collapsed ? 'Dashboard' : undefined}
                  >
                    <LayoutDashboard />
                    {!collapsed && <span>Dashboard</span>}
                  </NavLink>
                </div>
                <BenefactorSectionNav
                  collapsed={collapsed}
                  variant="sidebar"
                  enabledFeatures={enabledFeatures}
                  ectUnread={ectUnread}
                />
                {/* ACCOUNT section pinned below — anchored, not user-reorderable */}
                {!collapsed && (
                  <div className="nav-section">
                    <div className="nav-section-title">ACCOUNT</div>
                    {user?.partner_rep && (
                      <NavLink to="/pro/clients" className={({ isActive }) => `nav-item-sm ${isActive ? 'active' : ''}`} data-testid="nav-pro-clients"><Briefcase /><span>Client Setup</span></NavLink>
                    )}
                    <NavLink to="/settings" className={({ isActive }) => `nav-item-sm ${isActive ? 'active' : ''}`} data-testid="nav-settings"><Settings /><span>Settings</span></NavLink>
                    {subsVisible && <NavLink to="/subscription" className={({ isActive }) => `nav-item-sm ${isActive ? 'active' : ''}`} data-testid="nav-subscription"><CreditCard /><span>Subscription</span></NavLink>}
                    <NavLink to="/security-settings" className={({ isActive }) => `nav-item-sm ${isActive ? 'active' : ''}`} data-testid="nav-security-settings"><ShieldCheck /><span>Security Settings</span></NavLink>
                    <NavLink to="/support" className={({ isActive }) => `nav-item-sm ${isActive ? 'active' : ''}`} data-testid="nav-customer-support"><Headphones /><span>Customer Support</span></NavLink>
                  </div>
                )}
                {collapsed && (
                  <div className="nav-section">
                    {user?.partner_rep && (
                      <NavLink to="/pro/clients" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Client Setup" data-testid="nav-pro-clients"><Briefcase /></NavLink>
                    )}
                    <NavLink to="/settings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Settings" data-testid="nav-settings"><Settings /></NavLink>
                    {subsVisible && <NavLink to="/subscription" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Subscription" data-testid="nav-subscription"><CreditCard /></NavLink>}
                    <NavLink to="/security-settings" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Security Settings" data-testid="nav-security-settings"><ShieldCheck /></NavLink>
                    <NavLink to="/support" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title="Customer Support" data-testid="nav-customer-support"><Headphones /></NavLink>
                  </div>
                )}
              </>
            );
          }
          // Beneficiary side / operator / fallback — legacy flat list.
          return getNavSections().map((section, idx) => {
          const isAccountSection = section.title === 'ACCOUNT';
          return (
          <div key={idx} className="nav-section">
            {section.title && !collapsed && <div className="nav-section-title">{section.title}</div>}
            {section.items.map((item, itemIdx) => (
              <React.Fragment key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) => `${isAccountSection ? 'nav-item-sm' : 'nav-item'} ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon />
                  {!collapsed && <span>{item.label}</span>}
                  {item.badge > 0 && (
                    <span className="ml-auto min-w-[20px] h-5 rounded-full flex items-center justify-center text-[12px] font-bold px-1.5" style={{ background: '#d4af37', color: '#080e1a' }} data-testid={`ect-unread-badge`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </NavLink>
                {itemIdx < section.items.length - 1 && (
                  <div className="nav-divider" />
                )}
              </React.Fragment>
            ))}
          </div>
          );
        });
        })()}
        {/* Utility actions — Notifications, Theme, Collapse — placed
            inside the scrollable area beneath the ACCOUNT section so the
            scrollable feature list above gets back the vertical room it
            was losing to a bloated pinned footer. (Per founder directive,
            Apr 2026.) */}
        <div className="nav-section" data-testid="nav-utility-actions">
          <NotificationBell collapsed={collapsed} />
          <SidebarPillButton
            collapsed={collapsed}
            onClick={toggleTheme}
            data-testid="theme-toggle"
            icon={theme === 'dark' ? <Sun /> : <Moon />}
            label={`${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
          />
          <SidebarPillButton
            collapsed={collapsed}
            onClick={toggleCollapsed}
            data-testid="sidebar-collapse-toggle"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            icon={collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            label="Collapse"
          />
        </div>
      </nav>

      {/* Bottom Pinned Section */}
      <div className="sb-user">
        {/* ── Separator ── */}
        <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.08)', margin: '2px 0' }} />

        {/* Admin Portal Buttons — stacked above Sign Out */}
        {user?.role === 'admin' && (() => {
          const scopes = scopeArr(user?.admin_scope);
          const isFounder = scopes.includes('founder');
          const visiblePortals = isFounder
            ? ADMIN_PORTALS
            : ADMIN_PORTALS.filter(p => scopes.includes(p.scope) || (p.altScope && scopes.includes(p.altScope)));
          if (visiblePortals.length < 1) return null;
          const currentPath = window.location.pathname;
          const _activeViewScope = scopes;
          return (
            <>
              {!collapsed && (
                <div style={{ fontSize: 14, fontWeight: 700, color: '#8895A7', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6, paddingLeft: 4 }}>
                  {visiblePortals.length > 1 ? 'Switch Portal' : 'My Portal'}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {visiblePortals.map(portal => {
                  const scopeParam = new URLSearchParams(window.location.search).get('scope');
                  const isActive = portal.scope === 'founder'
                    ? !scopeParam && !currentPath.startsWith('/ops')
                    : scopeParam === portal.scope || (portal.altScope && scopeParam === portal.altScope);
                  return (
                    <button
                      key={portal.scope}
                      onClick={() => {
                        if (portal.scope === 'founder') {
                          navigate('/admin');
                        } else {
                          navigate(`/admin?scope=${portal.scope}`);
                        }
                      }}
                      data-testid={`portal-btn-${portal.scope}`}
                      className={`sb-pill w-full ${collapsed ? 'justify-center' : ''}`}
                      style={{
                        background: isActive
                          ? `${portal.color}15`
                          : undefined,
                        borderColor: isActive ? `${portal.color}40` : undefined,
                        color: isActive ? portal.color : undefined,
                        fontWeight: isActive ? 700 : undefined,
                        padding: collapsed ? undefined : '10px 16px',
                        flexDirection: collapsed ? undefined : 'column',
                        alignItems: collapsed ? undefined : 'center',
                        gap: collapsed ? undefined : 2,
                      }}
                    >
                      {collapsed ? (
                        <Shield className="w-[18px] h-[18px]" style={{ color: isActive ? portal.color : undefined }} />
                      ) : (
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{portal.label}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* Switch View — Portal Pills (Benefactor/Beneficiary) */}
        {(() => {
          const ownedEstates = benEstates.filter(e => e.user_role_in_estate === 'owner');
          const beneficiaryEstates = benEstates.filter(e => e.user_role_in_estate === 'beneficiary' || e.is_beneficiary_estate);
          const showSwitch = ownedEstates.length > 0 || beneficiaryEstates.length > 0;
          if (!showSwitch) return null;
          const isBenActive = window.location.pathname.startsWith('/beneficiary');
          const isBenefactorActive = !isBenActive && ownedEstates.length > 0;
          // Determine which estate is currently active
          const currentEstateId = localStorage.getItem('selected_estate_id')
            || user?.primary_estate_id
            || (ownedEstates.length > 0 ? ownedEstates[0].id : null);
          return (
            <>
              {!collapsed && (
                <div style={{ fontSize: 14, fontWeight: 700, color: '#8895A7', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6, paddingLeft: 4 }}>
                  Switch View
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, position: 'relative' }}>
                {/* Benefactor Portal — always opens picker with estates + create new */}
                {ownedEstates.length > 0 && (
                  <button onClick={() => setEstatePickerOpen(!estatePickerOpen)}
                  data-testid="switch-benefactor-portal"
                  className={`sb-pill w-full ${collapsed ? 'justify-center' : ''}`}
                  style={{
                    background: isBenefactorActive
                      ? theme === 'light' ? 'rgba(254,249,231,0.95)' : 'rgba(var(--gold-rgb), 0.1)'
                      : undefined,
                    borderColor: isBenefactorActive
                      ? theme === 'light' ? 'rgba(var(--gold-rgb), 0.55)' : 'rgba(var(--gold-rgb), 0.4)'
                      : undefined,
                    color: isBenefactorActive
                      ? theme === 'light' ? '#7a5c00' : '#d4af37'
                      : undefined,
                    boxShadow: isBenefactorActive && theme === 'light'
                      ? '0 1px 2px rgba(212,175,55,0.10), 0 1px 6px rgba(212,175,55,0.06)'
                      : undefined,
                    fontWeight: isBenefactorActive ? 700 : undefined,
                    padding: collapsed ? undefined : '10px 16px',
                    flexDirection: collapsed ? undefined : 'column',
                    alignItems: collapsed ? undefined : 'center',
                    gap: collapsed ? undefined : 2,
                  }}>
                    {collapsed ? (
                      <Shield className="w-[18px] h-[18px]" style={{ color: isBenefactorActive ? (theme === 'light' ? '#7a5c00' : '#d4af37') : undefined }} />
                    ) : (
                      <>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>My Benefactor Portal</span>
                        <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.5 }}>{ownedEstates.length} estate{ownedEstates.length !== 1 ? 's' : ''}</span>
                      </>
                    )}
                  </button>
                )}

                {/* Estate Picker — always shows estates + create new */}
                {estatePickerOpen && ownedEstates.length > 0 && (
                  <>
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
                      onClick={() => setEstatePickerOpen(false)} />
                    <div style={{
                      position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 4,
                      background: 'var(--bg2)', border: '1px solid var(--b2)',
                      borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 100,
                      padding: 8,
                    }} data-testid="estate-picker">
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--t5)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6, paddingLeft: 4 }}>
                        Select Estate
                      </div>
                      {[...ownedEstates].sort((a, b) => {
                        const pid = user?.primary_estate_id;
                        if (a.id === pid) return -1;
                        if (b.id === pid) return 1;
                        return 0;
                      }).map(estate => {
                        const isCurrent = currentEstateId === estate.id;
                        const isPrimary = user?.primary_estate_id === estate.id;
                        return (
                        <div key={estate.id} className="flex items-center gap-1 mb-1">
                          <button
                            onClick={() => {
                              localStorage.setItem('selected_estate_id', estate.id);
                              localStorage.removeItem('beneficiary_estate_id');
          localStorage.removeItem('beneficiary_feature_access');
                              localStorage.setItem('carryon_last_portal', 'benefactor');
                              setEstatePickerOpen(false);
                              clearCache();
                              navigate('/dashboard');
                              window.location.reload();
                            }}
                            data-testid={`pick-estate-${estate.id}`}
                            className="flex-1 flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors"
                            style={{
                              color: isCurrent ? '#d4af37' : 'var(--t)',
                              background: isCurrent ? 'rgba(var(--gold-rgb), 0.15)' : 'transparent',
                              border: isCurrent ? '2px solid rgba(var(--gold-rgb), 0.5)' : '1px solid transparent',
                              fontWeight: isCurrent ? 700 : 500,
                            }}
                          >
                            {isCurrent && <Check className="w-4 h-4 flex-shrink-0" style={{ color: '#d4af37' }} />}
                            <span>{estate.name || 'Estate'}{isPrimary ? ' ★' : ''}</span>
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                await apiClient.put(`${API_URL}/estates/set-primary/${estate.id}`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('carryon_token')}` } });
                                clearCache();
                                await refreshUser();
                              } catch {}
                            }}
                            title={isPrimary ? 'Primary estate' : 'Set as primary'}
                            className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md transition-colors"
                            style={{ color: isPrimary ? '#d4af37' : 'var(--t5)' }}
                          >
                            <Star className="w-3.5 h-3.5" style={{ fill: isPrimary ? '#d4af37' : 'none', transition: 'fill 0.2s' }} />
                          </button>
                        </div>
                        );
                      })}
                      <div style={{ height: 1, background: 'var(--b2)', margin: '6px 4px' }} />
                      <button
                        onClick={() => {
                          setEstatePickerOpen(false);
                          navigate('/create-estate');
                        }}
                        data-testid="create-new-estate-btn"
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
                        style={{ color: '#d4af37' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--gold-rgb), 0.08)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <Plus className="w-4 h-4" /> Create New Estate
                      </button>
                    </div>
                  </>
                )}

                {/* Beneficiary Portal — opens the Estate Plan Network
                    hub at /beneficiary (orbit view of all benefactors).
                    The hub has the canonical "tap a benefactor → open
                    that estate's dashboard" interaction. Previously
                    this jumped straight to /beneficiary/dashboard
                    (causing a redirect loop / white screen when the
                    user had multiple connected estates). */}
                {beneficiaryEstates.length > 0 && (
                  <button onClick={() => {
                    localStorage.removeItem('selected_estate_id');
                    localStorage.removeItem('beneficiary_estate_id');
          localStorage.removeItem('beneficiary_feature_access');
                    localStorage.setItem('carryon_last_portal', 'beneficiary');
                    clearCache();
                    navigate('/beneficiary');
                  }}
                  data-testid="switch-beneficiary-portal"
                  className={`sb-pill w-full ${collapsed ? 'justify-center' : ''}`}
                  style={{
                    background: isBenActive
                      ? theme === 'light' ? 'rgba(var(--gold-rgb), 0.15)' : 'rgba(var(--gold-rgb), 0.1)'
                      : undefined,
                    borderColor: isBenActive ? 'rgba(var(--gold-rgb), 0.4)' : undefined,
                    color: isBenActive
                      ? theme === 'light' ? '#1a2744' : '#d4af37'
                      : undefined,
                    fontWeight: isBenActive ? 700 : undefined,
                    padding: collapsed ? undefined : '10px 16px',
                    flexDirection: collapsed ? undefined : 'column',
                    alignItems: collapsed ? undefined : 'center',
                    gap: collapsed ? undefined : 2,
                  }}>
                    {collapsed ? (
                      <Users className="w-[18px] h-[18px]" style={{ color: isBenActive ? (theme === 'light' ? '#1a2744' : '#d4af37') : undefined }} />
                    ) : (
                      <>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>My Beneficiary Portal</span>
                        {beneficiaryEstates.length > 1 && <span style={{ fontSize: 11, opacity: 0.5 }}>{beneficiaryEstates.length} estates</span>}
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          );
        })()}

        {/* ── Separator ── */}
        <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.08)', margin: '2px 0' }} />

        {/* Public Device Mode — panic switch directly above Sign Out.
            Self-gates if the user has no estate. */}
        <PublicDeviceModeMenuButton flavor="sidebar" collapsed={collapsed} />

        {/* Sign Out — pill button, danger style */}
        <SidebarPillButton
          collapsed={collapsed}
          onClick={handleLogout}
          data-testid="logout-button"
          variant="danger"
          icon={<LogOut />}
          label="Sign Out"
        />
      </div>
    </aside>
  );
};

export default Sidebar;

// Beta Banner Component
const BetaBanner = ({ collapsed }) => {
  const [isBeta, setIsBeta] = useState(null);
  
  useEffect(() => {
    const check = async () => {
      try {
        const res = await apiClient.get(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/plans`);
        setIsBeta(res.data.beta_mode);
      } catch { setIsBeta(null); }
    };
    check();
    // Poll every 15s so toggling beta in Founder tab reflects immediately
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, []);

  if (!isBeta) return null;

  if (collapsed) {
    return (
      <div className="mx-auto my-2 w-9 h-9 rounded-lg flex items-center justify-center" 
        style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}
        title="BETA = FREE">
        <span className="text-base font-bold text-[var(--gn2)]" style={{ fontFamily: 'serif' }}>&beta;</span>
      </div>
    );
  }

  return (
    <div className="mx-3 mb-2 px-2.5 py-1.5 rounded-lg text-center" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }} data-testid="beta-banner">
      <span className="text-[12px] font-bold text-[var(--gn2)] tracking-wider">BETA = FREE</span>
    </div>
  );
};
